import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DefaultService, StrategyGuideOutput } from '../generated-api';
import { ArrowPathIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useJobPoller } from '../hooks/useJobPoller';

interface StrategyGuidePageProps {
  gameId: string;
  gameName: string;
}

type SkillLevel = 'beginner' | 'intermediate' | 'advanced';

const LEVELS: { key: SkillLevel; label: string; badge: string; badgeBg: string; badgeText: string }[] = [
  { key: 'beginner', label: 'Beginner', badge: 'New', badgeBg: 'bg-success-50', badgeText: 'text-success-700' },
  { key: 'intermediate', label: 'Intermediate', badge: 'Mid', badgeBg: 'bg-warning-50', badgeText: 'text-warning-700' },
  { key: 'advanced', label: 'Advanced', badge: 'Pro', badgeBg: 'bg-error-50', badgeText: 'text-error-700' },
];

const StrategyGuidePage: React.FC<StrategyGuidePageProps> = ({ gameId, gameName }) => {
  const [hasStrategy, setHasStrategy] = useState(false);
  const [strategyGuide, setStrategyGuide] = useState<StrategyGuideOutput['strategyGuide'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<SkillLevel>('beginner');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const loadStrategyGuide = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await DefaultService.getStrategyGuideGamesGameIdStrategyGet({ gameId });
      setHasStrategy(data.hasStrategy);
      setStrategyGuide(data.strategyGuide || null);
    } catch (error) {
      console.error('Failed to load strategy guide:', error);
    } finally {
      setIsLoading(false);
    }
  }, [gameId]);

  const strategyPoller = useJobPoller(gameId, 'strategy_generation', loadStrategyGuide);

  useEffect(() => {
    loadStrategyGuide();
  }, [loadStrategyGuide]);

  // Track scroll position for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGenerateStrategy = async () => {
    try {
      const response = await DefaultService.generateStrategyGuideGamesGameIdStrategyGeneratePost({ gameId });
      strategyPoller.startJob(response.jobId);
    } catch (error: any) {
      console.error('Failed to generate strategy guide:', error);
      alert(error?.body?.detail || error.message || 'Failed to generate strategy guide. Make sure rules are uploaded first.');
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Find the first available tab when guide loads
  useEffect(() => {
    if (strategyGuide) {
      if (strategyGuide.beginner) setActiveTab('beginner');
      else if (strategyGuide.intermediate) setActiveTab('intermediate');
      else if (strategyGuide.advanced) setActiveTab('advanced');
    }
  }, [strategyGuide]);

  const availableLevels = LEVELS.filter((l) => strategyGuide?.[l.key]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={contentRef}>
      {strategyPoller.isProcessing ? (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="flex items-center gap-3 py-12 justify-center text-muted">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
            <span>{strategyPoller.statusText}</span>
          </div>
        </div>
      ) : strategyPoller.error ? (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="p-4 bg-error-50 border border-error-500/20 rounded-button text-sm text-error-700">
            {strategyPoller.error}
          </div>
        </div>
      ) : hasStrategy && strategyGuide ? (
        <>
          {/* Tab navigation - sticky on mobile */}
          {availableLevels.length > 1 && (
            <div className="sticky top-0 z-10 bg-surface-subtle/95 backdrop-blur-sm -mx-4 px-4 py-2 sm:mx-0 sm:px-0 sm:static sm:bg-transparent sm:backdrop-blur-none sm:py-0">
              <div className="flex items-center gap-2">
                {availableLevels.map((level) => (
                  <button
                    data-track="strategy_tab"
                    key={level.key}
                    onClick={() => setActiveTab(level.key)}
                    className={`flex-1 sm:flex-none px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      activeTab === level.key
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-surface text-body border border-edge hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    <span>{level.label}</span>
                  </button>
                ))}
                <button
                  onClick={handleGenerateStrategy}
                  disabled={strategyPoller.isProcessing}
                  className="ml-auto flex-shrink-0 p-2 text-disabled hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Regenerate Strategy Guide"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${strategyPoller.isProcessing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          )}

          {/* Active section content */}
          {availableLevels.map((level) => {
            if (level.key !== activeTab) return null;
            const content = strategyGuide[level.key];
            if (!content) return null;

            return (
              <div
                key={level.key}
                className="bg-surface border border-edge rounded-card p-4 sm:p-6 shadow-card"
              >
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="font-serif text-lg sm:text-xl font-bold text-heading">
                    {level.label}
                  </h3>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${level.badgeBg} ${level.badgeText}`}>
                    {level.badge}
                  </span>
                </div>
                <div className="prose prose-stone prose-sm sm:prose-base max-w-none prose-headings:font-serif prose-headings:text-heading prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-800 prose-img:rounded-card">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="text-center py-12">
            <p className="text-lg text-body mb-2">No strategy guide available yet</p>
            <p className="text-sm text-muted mb-6">Upload game rules first, then generate a strategy guide</p>
            <button
              onClick={handleGenerateStrategy}
              disabled={strategyPoller.isProcessing}
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-white font-medium rounded-button shadow-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate Strategy Guide
            </button>
          </div>
        </div>
      )}

      {/* Scroll to top FAB */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-4 sm:bottom-8 sm:right-8 z-20 p-3 bg-surface border border-edge rounded-full shadow-lg text-body hover:text-primary-600 hover:border-primary-300 transition-all active:scale-95"
          aria-label="Scroll to top"
        >
          <ChevronUpIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default StrategyGuidePage;
